import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
    selector: 'mh-core-lib-textarea-input',
    templateUrl: './textarea-input.component.html',
    styleUrls: ['./textarea-input.component.scss'],
    standalone: false
})
export class CoreLibTextareaInputComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('textAreaInput', { static: false }) textarea: ElementRef;

  private _users;
  defaultForm: FormGroup<{ defaultControl: FormControl<any>; }>;
  @Input() set users(newValue) {
    if (newValue) {
      this._users = newValue;
      this.createItemsForMentions();
    }
  }
  get users() {
    return this._users;
  }

  @Input()
  formGroup: FormGroup;

  @Input()
  keyControl;

  @Input()
  placeholder = '';

  @Input()
  rows: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 = 3;

  @Input()
  withMentions = false;

  @Input()
  inputClass;

  @Input()
  value;

  @Input()
  maxLength: number;

  @Input()
  customContainerClass: string;

  @Input()
  autoRezise = false;

  @Output()
  mentionStateChanged = new EventEmitter();

  @Output()
  textareaReady = new EventEmitter();

  @Input() isReadonly: boolean = false;
  
  // Configuración para ngx-mentions
  mentionsItems: any[] = [];
  filteredMentions: any[] = [];
  mentionState = [];
  subscription = new Subscription();
  
  // Configuración de ngx-mentions
  ngxMentionsConfig = [
    {
      triggerCharacter: '@',
      getChoiceLabel: (user: any) => {
        return `@[${user.email}:${btoa(String(user.id))}]`;
      },
    },
  ];

  ngOnInit(): void {
    if (this.withMentions) {
      this.createItemsForMentions();
      this.formatContent(null, this.formGroup.get(this.keyControl).value )
    }

    this.defaultForm = new FormGroup({
      defaultControl: new FormControl(this.value)
    });
  }

  ngAfterViewInit(): void {
    this.textareaReady.emit(this.textarea);
    if (this.autoRezise) {
      this.subscription = this.formGroup.get(this.keyControl).valueChanges.subscribe((result) => {
        if (result) {
          this.textarea.nativeElement.style.height = this.textarea.nativeElement.scrollHeight + 'px';
        } else {
          this.textarea.nativeElement.style.height = '40px';
        }
      });
    }
  }

  createItemsForMentions(): void {
    this.mentionsItems = this.users.map(({ id, first_name, last_name, email, company_position }) => ({
      email,
      id,
      mentionId: `@[${email}:${btoa(String(id))}]`,
      name: `${first_name} ${last_name} (${company_position})`,
      label: `${first_name} ${last_name} (${company_position})`,
    }));
    this.filteredMentions = [...this.mentionsItems]; // Inicialmente mostrar todos
  }

  onMentionSearch(event: { searchText: string; triggerCharacter: string }): void {
    const searchText = event.searchText.toLowerCase();
    this.filteredMentions = this.mentionsItems.filter(user =>
      user.name.toLowerCase().includes(searchText) ||
      user.email.toLowerCase().includes(searchText)
    );
  }

  selectMentionFromMenu(user: any, selectChoiceFn: Function): void {
    selectChoiceFn(user);
    this.selectMention({ choice: user });
  }

  onSelectedChoicesChange(choices: any[]): void {
    // Mantener sincronizado con la lógica de formatContent
    if (choices && choices.length > 0) {
      this.mentionStateChanged.emit(this.mentionState);
    }
  }

  formatContent(event?, initialContent = null): void {
    const value = event?.target?.value ?? initialContent;
    if (value && value.length > 0) {
      const controlText = this.formGroup.get(this.keyControl);
      const matches = value.match(/\@\[(.*?)\]/g) || [];
      let newValue = value; // Inicializar con el valor original
      let hasChanges = false;

      matches.forEach((match: string) => {
        try {
          let match_hash = match.replace(/\[|\]|^\@/g, '');
          const hashContent = match_hash?.split(':') || '';
          const hashId = hashContent[1];
          if( hashId ) {
            const base64Id = +atob(hashId);
            const hash = match;
            const user = this.users.find((findedUser) => findedUser.id === base64Id);
            
            if (user) {
              // Reemplazar TODAS las ocurrencias del hash en el valor acumulado
              const replacement = `@[${user.first_name} ${user.last_name}]`;
              newValue = newValue.replace(hash, replacement);
              hasChanges = true;

              const key = `@[${user.first_name} ${user.last_name}]`
              const hashValue = `@[${match_hash}]`

              if (!this.mentionState.find((findedMention) => findedMention.key === key)) {
                this.mentionState = [...this.mentionState, { key, value: hashValue }];
              }
            }
          }

        } catch(error) {
          const user = match;
          if (!this.mentionState.find((mention) => mention.key === user)) {
            newValue = newValue.replace(user, '');
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        controlText.setValue(newValue);
        this.mentionStateChanged.emit(this.mentionState);
      }
    }
  }

  formatMention(item: any): string {
    return `@[${item.email}:${btoa(String(item.id))}]`;
  }

  selectMention(mention): void {
    const mentionData = mention?.choice || mention;
    const value = mentionData.mentionId || `@[${mentionData.email}:${btoa(String(mentionData.id))}]`;
    const user = this.users.find((findedUser) => findedUser.id === (mentionData.id));
    if (user) {
      const key = `@[${user.first_name} ${user.last_name}]`;
      if (!this.mentionState.find((findedMention) => findedMention.key === key)) {
        this.mentionState = [...this.mentionState, { key, value }];
        this.mentionStateChanged.emit(this.mentionState);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
